import classNames from 'classnames';
import { graphql, Link } from 'gatsby';
import {
  IS_PRO,
  REGION,
  RegionEnum,
} from 'gatsby-theme-hem-store/src/constants';
import { IconHem } from 'hem-ui';
import { Menu, Search } from 'lucide-react';
import React, { useEffect } from 'react';

import { CommandMenu, useCommandMenu } from '@/components/CommandMenu';
import { CommandShortcut } from '@/components/ui/command';
import { sendGtmSearchBarFocusedEvent } from '@/utils/sendGtmEvents';

import { PanelEnum, usePanelStore } from '../Panels/Panels';
import { Touchable } from '../Touchable';
import { CartLink } from './CartLink';
import { HemProLogo } from './HemProLogo';
import { HemXLogo } from './HemXLogo';
import { LoginLink } from './LoginLink';
import {
  desktopLogo,
  inner,
  leftContainer,
  logo,
  logoContainer,
  nav,
  rightContainer,
} from './Navbar.module.scss';
import { TopNav } from './TopNav';

type Props = {
  isHomePage: boolean;
  isHemX: boolean;
  location: Location;
  data: Queries.NavbarQuery;
};

export const Navbar: React.FC<Props> = ({ isHomePage, isHemX, data }) => {
  const { openCommandMenu, commandMenuIsOpen } = useCommandMenu((state) => ({
    openCommandMenu: state.open,
    commandMenuIsOpen: state.isOpen,
  }));

  const { setCurrentlyOpenPanel } = usePanelStore();

  const openMenu = () => setCurrentlyOpenPanel(PanelEnum.Menu);

  useEffect(() => {
    if (commandMenuIsOpen) {
      sendGtmSearchBarFocusedEvent();
    }
  }, [commandMenuIsOpen]);

  return (
    <>
      <TopNav
        announcement={data.campaign.announcementBar}
        announcementLoggedIn={data.campaign.announcementBarLoggedIn}
        theme={data.campaign.announcementBarTheme}
      />
      <nav className={classNames(nav, 'no-print')} id="navbar">
        <div className={inner}>
          <div className={leftContainer}>
            <Touchable
              onClick={openMenu}
              aria-label="Menu"
              data-selector="Navigation menu button"
              className="text-black transition-colors hover:text-black-600"
            >
              <Menu size={20} />
            </Touchable>

            <Touchable
              onClick={openCommandMenu}
              aria-label="Menu"
              data-selector="Navigation search button"
              className="block text-black transition-colors hover:text-black-600 md:!hidden"
            >
              <Search size={20} />
            </Touchable>

            <div className={desktopLogo}>
              {isHomePage === true ? (
                <h1 className="flex justify-center">
                  <span className="sr-only">Hem</span>
                  {IS_PRO ? (
                    <HemProLogo aria-hidden="true" />
                  ) : isHemX ? (
                    <HemXLogo />
                  ) : (
                    <IconHem aria-hidden="true" />
                  )}
                </h1>
              ) : (
                <Link
                  className="flex justify-center"
                  to="/"
                  title="Go to homepage"
                >
                  <span className="sr-only">Hem</span>
                  {IS_PRO ? (
                    <HemProLogo aria-hidden="true" />
                  ) : isHemX ? (
                    <HemXLogo />
                  ) : (
                    <IconHem aria-hidden="true" />
                  )}
                </Link>
              )}
            </div>
          </div>
          <button
            onClick={openCommandMenu}
            className="col-[4_/_10] hidden h-full items-center justify-between shadow-[0_2px_0_0_#000] md:flex"
          >
            <div className="flex items-center space-x-4">
              <Search size={20} className="text-black-400" />
              <p className="line-clamp-1 text-left text-lg text-black-400">
                Shop furniture, lighting, rugs, and accessories
              </p>
            </div>
            <div className="flex flex-nowrap space-x-1">
              <CommandShortcut>⌘</CommandShortcut>
              <CommandShortcut>K</CommandShortcut>
            </div>
            <CommandMenu />
          </button>
          <div className={logoContainer}>
            {isHomePage === true ? (
              <h1>
                <span className="sr-only">Hem</span>
                {IS_PRO ? (
                  <HemProLogo aria-hidden="true" />
                ) : isHemX ? (
                  <HemXLogo />
                ) : (
                  <IconHem aria-hidden="true" />
                )}
              </h1>
            ) : (
              <Link className={logo} to="/" title="Go to homepage">
                <span className="sr-only">Hem</span>
                {IS_PRO ? (
                  <HemProLogo aria-hidden="true" />
                ) : isHemX ? (
                  <HemXLogo />
                ) : (
                  <IconHem aria-hidden="true" />
                )}
              </Link>
            )}
          </div>
          <div className={rightContainer}>
            {REGION !== RegionEnum.Ca && <LoginLink />}
            <CartLink />
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;

export const query = graphql`
  query Navbar($id: String!) {
    campaign: contentfulCampaign(id: { eq: $id }) {
      announcementBar
      announcementBarLoggedIn
      announcementBarTheme
    }
  }
`;
